import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layers } from 'lucide-react';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { fmtInt, fmtUsd } from '@/lib/format';

interface YearStamp {
  vintage: number;
  present: boolean;
  wine_id: number | null;
}

interface VerticalSet {
  complete: boolean;
  present_count: number;
  years: YearStamp[];
}

interface VerticalSeries {
  wine_name: string;
  winery_id: number;
  winery_name: string | null;
  format: string;
  grape_summary: string | null;
  vintage_min: number;
  vintage_max: number;
  vintage_count: number;
  set_count: number;
  complete_set_count: number;
  total_stock: number;
  total_cost_usd: number;
  total_estimated_usd: number;
  sets: VerticalSet[];
}

interface VerticalsResponse {
  data: VerticalSeries[];
  meta: {
    count: number;
    total_sets: number;
    complete_sets: number;
    total_stock: number;
    total_cost_usd: number;
    total_estimated_usd: number;
  };
}

function useVerticals() {
  return useQuery({
    queryKey: ['verticals'],
    queryFn: async () => {
      const { data } = await api.get<VerticalsResponse>('/verticals');
      return data;
    },
  });
}

function YearStampPill({ year, present, wineId }: { year: number; present: boolean; wineId: number | null }) {
  // Más chico en mobile, mismo tamaño que antes en desktop.
  const base =
    'inline-flex items-center justify-center min-w-[42px] md:min-w-[58px] px-1.5 md:px-2 py-0.5 md:py-1 rounded-md border text-[11px] md:text-xs font-medium tabular-nums shrink-0';
  const styles = present
    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
    : 'bg-notion-50 border-notion-200 text-notion-400 line-through decoration-notion-300';

  if (present && wineId) {
    return (
      <Link to={`/wines/${wineId}`} className={cn(base, styles, 'hover:bg-emerald-100')}>
        {year}
      </Link>
    );
  }
  return <span className={cn(base, styles)}>{year}</span>;
}

export function VerticalsPage() {
  const { data, isLoading } = useVerticals();

  if (isLoading) {
    return <div className="p-8 text-notion-500">Cargando…</div>;
  }

  const series = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div className="p-4 md:p-8 max-w-6xl">
      <header className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-medium flex items-center gap-2">
          <Layers size={20} className="text-notion-500" /> Verticales
        </h1>
        <p className="text-xs md:text-sm text-notion-500 mt-1">
          Cada fila es un set tomando 1 botella por añada del rango. Verde = tenés esa añada,
          gris = falta.
        </p>
      </header>

      {meta && series.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
          <div className="kpi">
            <div className="kpi-label">Series</div>
            <div className="kpi-value">{fmtInt(meta.count)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Sets completos</div>
            <div className="kpi-value">
              {fmtInt(meta.complete_sets)}
              <span className="text-sm text-notion-500 font-normal">
                {' '}/ {fmtInt(meta.total_sets)}
              </span>
            </div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Invertido (USD)</div>
            <div className="kpi-value">{fmtUsd(meta.total_cost_usd)}</div>
          </div>
          <div className="kpi">
            <div className="kpi-label">Valor estimado (USD)</div>
            <div className="kpi-value">{fmtUsd(meta.total_estimated_usd)}</div>
          </div>
        </div>
      )}

      {series.length === 0 ? (
        <div className="card p-12 text-center text-notion-500">
          <Layers size={32} className="mx-auto text-notion-300 mb-3" />
          <p>Todavía no tenés series con 2+ añadas distintas en tu cava.</p>
          <p className="text-xs mt-2">
            Una serie aparece cuando tenés al menos 2 añadas distintas del mismo vino,
            bodega y formato — con stock en cada una.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {series.map((s) => (
            <article
              key={`${s.winery_id}-${s.wine_name}-${s.format}-${s.grape_summary ?? ''}`}
              className="card p-3 md:p-5"
            >
              <header className="flex flex-wrap items-start justify-between gap-2 md:gap-3 mb-3 md:mb-4">
                <div className="min-w-0">
                  <h2 className="font-medium text-notion-800 leading-tight">
                    {s.wine_name}{' '}
                    <span className="text-notion-500 text-xs md:text-sm font-normal">
                      · {s.format}
                    </span>
                  </h2>
                  <p className="text-xs md:text-sm text-notion-500 truncate">
                    {s.winery_name ?? '—'}
                    {s.grape_summary && (
                      <span className="text-notion-400"> · {s.grape_summary}</span>
                    )}
                  </p>
                </div>
                <div className="text-right text-xs md:text-sm shrink-0">
                  <p className="text-notion-800 font-medium tabular-nums">
                    {s.vintage_min}
                    <span className="text-notion-400 px-1">→</span>
                    {s.vintage_max}
                  </p>
                  <p className="text-[10px] md:text-[11px] text-notion-500">
                    {s.complete_set_count}/{s.set_count} completos · {fmtInt(s.total_stock)} bot
                  </p>
                </div>
              </header>

              <div className="space-y-1.5 overflow-x-auto">
                {s.sets.map((set, idx) => (
                  <div
                    key={idx}
                    className={cn(
                      'flex items-center gap-1.5 py-1',
                      set.complete && 'pl-2 border-l-2 border-emerald-300',
                    )}
                  >
                    {set.years.map((y) => (
                      <YearStampPill
                        key={y.vintage}
                        year={y.vintage}
                        present={y.present}
                        wineId={y.wine_id}
                      />
                    ))}
                    <span
                      className={cn(
                        'text-[11px] ml-2',
                        set.complete ? 'text-emerald-700' : 'text-notion-500',
                      )}
                    >
                      {set.complete ? 'completo' : `${set.present_count}/${s.vintage_count}`}
                    </span>
                  </div>
                ))}
              </div>

              <footer className="mt-3 md:mt-4 pt-2 md:pt-3 border-t border-notion-100 grid grid-cols-2 gap-3 text-xs md:text-sm">
                <div>
                  <p className="text-notion-500 text-[10px] md:text-xs">Invertido (USD)</p>
                  <p className="font-medium">{fmtUsd(s.total_cost_usd)}</p>
                </div>
                <div>
                  <p className="text-notion-500 text-[10px] md:text-xs">Valor estimado (USD)</p>
                  <p className="font-medium">{fmtUsd(s.total_estimated_usd)}</p>
                </div>
              </footer>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
